import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/redux'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { getDashboardOrganizationById } from '@/services/adminDashboardService'
import { formatCurrency } from '@/helpers/billing'
import { formatDateTimeSafe } from '@/pages/Dashboard/helpers/dateRange'
import type { DashboardOrganizationSubscriptionResponse } from '@/types/adminDashboard'

export interface OrganizationDetailModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string | null
  token: string | null
}

function selectOrganizationDetail(id: string | null) {
  return (state: RootState) =>
    id ? state.adminDashboard.organizationDetailById[id] ?? null : null
}

export function OrganizationDetailModal({
  open,
  onOpenChange,
  organizationId,
  token
}: OrganizationDetailModalProps) {
  const dispatch = useDispatch<AppDispatch>()
  const data = useSelector(selectOrganizationDetail(organizationId))
  const loading = useSelector((state: RootState) => state.app.loading)

  useEffect(() => {
    if (!open || !organizationId || !token) return
    if (data) return
    dispatch(getDashboardOrganizationById(token, organizationId))
  }, [open, organizationId, token, dispatch, data])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto text-card-foreground">
        <DialogHeader>
          <DialogTitle>Organization details</DialogTitle>
        </DialogHeader>
        {loading && <p className="text-sm text-card-foreground/85">Loading…</p>}
        {!loading && data && (
          <div className="space-y-4">
            <dl className="grid gap-2 text-sm">
              <div>
                <dt className="font-medium text-card-foreground/85">Name</dt>
                <dd>{data.organizationName}</dd>
              </div>
              <div>
                <dt className="font-medium text-card-foreground/85">Organization ID</dt>
                <dd className="font-mono text-xs">{data.organizationId}</dd>
              </div>
              <div>
                <dt className="font-medium text-card-foreground/85">Created</dt>
                <dd>{formatDateTimeSafe(data.createdAt)}</dd>
              </div>
              {data.createdByEmail && (
                <div>
                  <dt className="font-medium text-card-foreground/85">Created by</dt>
                  <dd>{data.createdByEmail}</dd>
                </div>
              )}
              <div>
                <dt className="font-medium text-card-foreground/85">MRR</dt>
                <dd>{formatCurrency(data.mrrCents / 100)}</dd>
              </div>
              <div>
                <dt className="font-medium text-card-foreground/85">Outstanding payments</dt>
                <dd>{formatCurrency(data.outstandingPaymentsAmountCents / 100)}</dd>
              </div>
            </dl>
            <div>
              <h4 className="mb-2 text-sm font-medium">Subscriptions</h4>
              {data.subscriptions.length === 0 ? (
                <p className="text-sm text-card-foreground/85">No active subscriptions</p>
              ) : (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Subscription ID</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Plan / pricing</TableHead>
                        <TableHead>Current period end</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.subscriptions.map((sub: DashboardOrganizationSubscriptionResponse) => (
                        <TableRow key={sub.subscriptionId}>
                          <TableCell className="font-mono text-xs">{sub.subscriptionId}</TableCell>
                          <TableCell>{sub.status}</TableCell>
                          <TableCell>
                            {sub.pricingModel === 'playbook_based'
                              ? `Playbook-based${sub.playbookCount != null ? ` (${sub.playbookCount})` : ''}`
                              : sub.planTier ?? '—'}
                          </TableCell>
                          <TableCell>
                            {formatDateTimeSafe(sub.currentPeriodEnd)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        )}
        {!loading && !data && open && organizationId && (
          <p className="text-sm text-card-foreground/85">No details available.</p>
        )}
      </DialogContent>
    </Dialog>
  )
}
